import React, { createContext, useContext, useState } from 'react';

import noop from 'lodash/noop';

import { useProjectContextValue } from '@qality/common-components/src/lib/QAlityPageConfiguration';
import { ProjectSelectOption, Status, TestType } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';

import { useFiltersBackup } from './useFiltersBackup';

export type TestCaseRepositoryFiltersContextValue = {
  isFilterApplied: boolean;
  projectFilter: Array<ProjectSelectOption>;
  textSearchFilter: string;
  statusesFilter: Array<Status>;
  testTypeFilter: Array<TestType>;
  applyFilters: () => void;
  undoFilters: () => void;
  setProjectFilter: (project: Array<ProjectSelectOption>) => void;
  setTextSearchFilter: (searchValue: string) => void;
  setStatusesFilter: (statuses: Array<Status>) => void;
  setTestTypeFilter: (testTypes: Array<TestType>) => void;
  resetFiltersToDefaults: () => void;
  getProjectsForQuery: () => ProjectSelectOption[];
  getStatusesForQuery: () => Status[];
  getTestTypeForQuery: () => TestType[];
  areFiltersEmpty: () => boolean;
  filterCount: number;
};

const initialContextValue: TestCaseRepositoryFiltersContextValue = {
  isFilterApplied: false,
  projectFilter: [],
  textSearchFilter: '',
  statusesFilter: [],
  testTypeFilter: [],
  applyFilters: () => undefined,
  undoFilters: () => undefined,
  setProjectFilter: (project: Array<ProjectSelectOption>) => noop(project),
  setTextSearchFilter: (searchValue: string) => noop(searchValue),
  setStatusesFilter: (statuses: Array<Status>) => noop(statuses),
  setTestTypeFilter: (testTypes: Array<TestType>) => noop(testTypes),
  resetFiltersToDefaults: () => undefined,
  getProjectsForQuery: () => [],
  getStatusesForQuery: () => [],
  getTestTypeForQuery: () => [],
  areFiltersEmpty: () => true,
  filterCount: 0,
};
export const TestCaseRepositoryFiltersContext =
  createContext<TestCaseRepositoryFiltersContextValue>(initialContextValue);

export const useTestCaseRepositoryFiltersContext =
  (): TestCaseRepositoryFiltersContextValue => {
    return useContext(TestCaseRepositoryFiltersContext);
  };

type Props = { children: React.ReactNode };

export default function TestCaseRepositoryFiltersProvider({
  children,
}: Props): JSX.Element {
  const ProjectSelectUtils = StorageOfUniqueServices.get('ProjectSelectUtils');

  const project = useProjectContextValue();

  const [projectFilter, setProjectFilter] = useState<
    Array<ProjectSelectOption>
  >(project ? [ProjectSelectUtils.mapProjectToSelectOption(project)] : []);
  const [statusesFilter, setStatusesFilter] = useState<Array<Status>>([]);
  const [testTypeFilter, setTestTypeFilter] = useState<Array<TestType>>([]);
  const [textSearchFilter, setTextSearchFilter] = useState('');
  const [isFilterApplied, setIsFilterApplied] = useState(true);
  const [filterCount, setFilterCount] = useState(0);

  const { filtersBackup, backupFilters } = useFiltersBackup({
    projects: projectFilter,
    statuses: statusesFilter,
    textSearch: textSearchFilter,
    testTypes: testTypeFilter,
  });

  const restoreFiltersFromBackup = () => {
    setProjectFilter(filtersBackup.projects);
    setStatusesFilter(filtersBackup.statuses);
    setTextSearchFilter(filtersBackup.textSearch);
    setTestTypeFilter(filtersBackup.testTypes);
  };

  const resetFiltersToDefaults = () => {
    setProjectFilter([]);
    setStatusesFilter([]);
    setTestTypeFilter([]);
    setTextSearchFilter('');
    backupFilters({
      projects: [],
      statuses: [],
      textSearch: '',
      testTypes: [],
    });
    setIsFilterApplied(true);
  };

  const getProjectsForQuery = () => {
    return projectFilter.length === 1 &&
      projectFilter[0].value === 'ALL_PROJECTS'
      ? []
      : projectFilter;
  };

  const getStatusesForQuery = () => {
    return statusesFilter ?? [];
  };

  const getTestTypeForQuery = () => {
    return testTypeFilter ?? [];
  };

  const areFiltersEmpty = (): boolean => {
    return (
      getProjectsForQuery().length === 0 &&
      getStatusesForQuery().length === 0 &&
      getTestTypeForQuery().length === 0 &&
      textSearchFilter === ''
    );
  };

  const applyFilters = () => {
    if (isFilterApplied) {
      return;
    }

    setIsFilterApplied(true);
    setFilterCount(filterCount + 1);

    backupFilters({
      projects: projectFilter,
      statuses: statusesFilter,
      textSearch: textSearchFilter,
      testTypes: testTypeFilter,
    });
  };

  return (
    <TestCaseRepositoryFiltersContext.Provider
      value={{
        isFilterApplied,
        projectFilter,
        textSearchFilter,
        statusesFilter,
        testTypeFilter,
        getProjectsForQuery,
        getStatusesForQuery,
        getTestTypeForQuery,
        areFiltersEmpty,
        applyFilters,
        filterCount,
        undoFilters: () => {
          setIsFilterApplied(true);
          restoreFiltersFromBackup();
        },
        setProjectFilter: (value: Array<ProjectSelectOption>) => {
          setIsFilterApplied(false);
          setProjectFilter(value);
        },
        setTextSearchFilter: (value: string) => {
          setIsFilterApplied(false);
          setTextSearchFilter(value);
        },
        setStatusesFilter: (value: Array<Status>) => {
          setIsFilterApplied(false);
          setStatusesFilter(value);
        },
        setTestTypeFilter: (value: Array<TestType>) => {
          setIsFilterApplied(false);
          setTestTypeFilter(value);
        },
        resetFiltersToDefaults,
      }}
    >
      {children}
    </TestCaseRepositoryFiltersContext.Provider>
  );
}
